import { Box, Button, Divider, ListSubheader, Tooltip } from '@mui/material';
import {
    CircleMinus,
    Crosshair,
    Download,
    Eye,
    EyeOff,
    Info,
} from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import MenuAction from '~/components/Menu/MenuAction';
import DosePropertiesPanel from './DosePropertiesPanel';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { AvailableDose } from '~/hooks/dose/useAvailableDoses';
import { doseNameOf } from '~/containers/viewer/explorer-sidebar/WorkflowSidebar/DoseStep/doseUtils';
import useConfirmedSelection from '~/hooks/datasetSelection/useConfirmedSelection';
import useDoseVisibility from '~/hooks/dose/useDoseVisibility';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import useViewportFocus from '~/hooks/useViewportFocus';
import { useMenuAnchor } from '~/hooks/useMenuAnchor';
import { useSidePropertiesPanel } from '~/hooks/useSidePropertiesPanel';
import { useRunningTasks } from '~/hooks/useRunningTasks';
import { doseOpacityState } from '~/state/atoms/dose/doseAtoms';
import ThemeProvider from '~/theme/ThemeProvider';
import { downloadDoseToDisk } from '~/utils/io/files/downloadDicom';
import { describeGenerationModel } from '~/utils/transforms/predictionModelsDataTransforms';
import {
    AiGeneratedMark,
    ConsoleMenu,
    consoleGroupSx,
    ImageMenuRow,
    ImageNameTrigger,
    joinedButtonSx,
    READOUT_ACCENT,
    squaredEdgeSx,
} from '~/viewportGrid';
import DoseMaxValue from './DoseMaxValue';
import DoseOpacityMenuRow from './DoseOpacityMenuRow';

/** Size of the eye that shows or hides the dose, in px. */
const VISIBILITY_ICON_PX = 12;

/**
 * A dose as a control of a console bar: an eye that shows or hides the
 * dose, joined to the dose's name, which opens its menu. The menu lists
 * `offered` to change the dose, then sets the dose's opacity, goes to its
 * maximum, shows its properties above the control, downloads it with its
 * plan, or unloads it. With no `dose` the control offers to load one. A
 * dose of another structure set asks before it makes that set the active
 * one.
 */
export default function DoseControl({
    dose,
    offered,
}: {
    /** The dose the row draws, if any. */
    dose: DoseMatrix | undefined;
    /** The doses the user can load here, the drawn one among them. */
    offered: ReadonlyArray<AvailableDose>;
}) {
    const intl = useIntl();
    const menu = useMenuAnchor();
    const groupRef = useRef<HTMLDivElement>(null);
    const downloads = useRunningTasks();
    const [panelAnchor, setPanelAnchor] = useState<HTMLElement | null>(null);
    const {
        isOpen: isPropertiesOpen,
        open: openProperties,
        close: closePropertiesPanel,
    } = useSidePropertiesPanel('dose');
    const opacity = useRecoilValue(doseOpacityState);
    const { isDoseVisible, toggleDoseVisibility } = useDoseVisibility();
    const { unloadDose } = useViewportEnvironment();
    const { pickDose } = useConfirmedSelection();
    const { focusOnPoint } = useViewportFocus();
    const current = offered.find(
        ({ item }) => item !== undefined && item === dose
    );

    const choose = (option: AvailableDose) => {
        menu.onClose();
        if (option === current || !option.item) {
            return;
        }
        pickDose(option.item);
    };

    const closeProperties = useCallback(() => {
        setPanelAnchor(null);
        closePropertiesPanel();
    }, [closePropertiesPanel]);

    const visibilityLabel = intl.formatMessage({
        id: isDoseVisible ? 'hideDose' : 'showDose',
    });
    const generatedBy = describeGenerationModel(dose?.generationModel);

    return (
        <Box
            ref={groupRef}
            data-testid="row-dose-control"
            sx={consoleGroupSx(0)}
        >
            {dose ? (
                <Tooltip title={visibilityLabel} placement="top">
                    <Button
                        variant="picker"
                        aria-label={visibilityLabel}
                        data-testid="row-dose-visibility"
                        onClick={toggleDoseVisibility}
                        sx={{
                            ...joinedButtonSx('right'),
                            px: 1,
                            color: isDoseVisible
                                ? READOUT_ACCENT
                                : 'text.secondary',
                        }}
                    >
                        {isDoseVisible ? (
                            <Eye size={VISIBILITY_ICON_PX} aria-hidden />
                        ) : (
                            <EyeOff size={VISIBILITY_ICON_PX} aria-hidden />
                        )}
                    </Button>
                </Tooltip>
            ) : null}
            <ImageNameTrigger
                modality="RTDOSE"
                label={current && doseNameOf(current)}
                placeholder={intl.formatMessage({ id: 'dose.load' })}
                lead={
                    generatedBy ? (
                        <AiGeneratedMark
                            images={[
                                {
                                    label: undefined,
                                    pairLabel: undefined,
                                    generatedBy,
                                },
                            ]}
                            data-testid="dose-ai-generated"
                        />
                    ) : undefined
                }
                tooltip={
                    dose ? (
                        <Box display="flex" flexDirection="column" gap={0.5}>
                            <DoseMaxValue dose={dose} />
                            <span>
                                {isDoseVisible
                                    ? intl.formatMessage(
                                          { id: 'dose.opacityShown' },
                                          { value: Math.round(opacity * 100) }
                                      )
                                    : intl.formatMessage({ id: 'dose.hidden' })}
                            </span>
                        </Box>
                    ) : undefined
                }
                ariaLabel={intl.formatMessage({ id: 'dose' })}
                open={menu.open}
                onOpen={() => groupRef.current && menu.onOpen(groupRef.current)}
                testId="row-dose-trigger"
                sx={{
                    ...(dose ? squaredEdgeSx('left') : {}),
                    // A hidden dose's name is faded, as the dose is not drawn.
                    opacity: dose && !isDoseVisible ? 0.6 : 1,
                }}
            />
            <ConsoleMenu
                anchorEl={menu.anchorEl}
                open={menu.open}
                onClose={menu.onClose}
                minWidth={264}
                align="right"
            >
                <ListSubheader>
                    {intl.formatMessage({
                        id: dose ? 'changeDose' : 'dose.load',
                    })}
                </ListSubheader>
                {offered.map((option) => {
                    const isCurrent = option === current;
                    return (
                        <ImageMenuRow
                            key={option.id}
                            selected={isCurrent}
                            onClick={() => choose(option)}
                            data-testid={`dose-option-${option.sopInstanceUID}`}
                            modality="RTDOSE"
                            label={doseNameOf(option)}
                            qualifier={
                                option.item && (
                                    <DoseMaxValue dose={option.item} />
                                )
                            }
                        />
                    );
                })}
                {dose
                    ? [
                          <Divider key="actions-divider" />,
                          <ListSubheader key="actions-title">
                              {intl.formatMessage({ id: 'dose' })}
                          </ListSubheader>,
                          <DoseOpacityMenuRow key="opacity" />,
                          // The menu reads `disabled` on its direct
                          // children to skip a row that cannot run when it
                          // chooses the row to focus.
                          <MenuAction
                              key="dose-go-to-max"
                              dense
                              icon={<Crosshair />}
                              label={intl.formatMessage({ id: 'dose.goToMax' })}
                              disabled={!dose.doseMaxPosition}
                              disabledReason={
                                  dose.doseMaxPosition
                                      ? undefined
                                      : intl.formatMessage({
                                            id: 'dose.maxUnknown',
                                        })
                              }
                              onClick={() => {
                                  menu.onClose();
                                  if (dose.doseMaxPosition) {
                                      focusOnPoint(dose.doseMaxPosition);
                                  }
                              }}
                              data-testid="image-action-dose-go-to-max"
                          />,
                          <MenuAction
                              key="dose-properties"
                              dense
                              icon={<Info />}
                              label={intl.formatMessage({ id: 'properties' })}
                              onClick={() => {
                                  const { anchorEl } = menu;
                                  menu.onClose();
                                  setPanelAnchor(anchorEl);
                                  openProperties();
                              }}
                              data-testid="image-action-dose-properties"
                          />,
                          // Download keeps the menu open with its progress.
                          <MenuAction
                              key="dose-download"
                              dense
                              icon={<Download />}
                              label={intl.formatMessage({ id: 'downloadDose' })}
                              busy={downloads.isRunning(dose.id)}
                              disabled={downloads.isRunning(dose.id)}
                              onClick={() =>
                                  downloads.run(dose.id, () =>
                                      downloadDoseToDisk(dose)
                                  )
                              }
                              data-testid="image-action-dose-download"
                          />,
                          <MenuAction
                              key="dose-unload"
                              dense
                              icon={<CircleMinus />}
                              label={intl.formatMessage({ id: 'unloadDose' })}
                              onClick={() => {
                                  menu.onClose();
                                  unloadDose();
                              }}
                              data-testid="image-action-dose-unload"
                          />,
                      ]
                    : null}
            </ConsoleMenu>
            {isPropertiesOpen && panelAnchor ? (
                // The panel is drawn under the app theme, not the footer's.
                <ThemeProvider>
                    <DosePropertiesPanel
                        anchorEl={panelAnchor}
                        onClose={closeProperties}
                    />
                </ThemeProvider>
            ) : null}
        </Box>
    );
}
